import { FormControl, FormLabel, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  DateCalendar,
  LocalizationProvider,
  PickersDay,
  PickersDayProps,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import * as React from 'react';
import { Control, Controller, FieldError } from 'react-hook-form';

import { esES } from '@mui/x-date-pickers/locales';

dayjs.extend(isBetweenPlugin);

interface CustomPickerDayProps extends PickersDayProps<Dayjs> {
  isSelected: boolean;
  isHovered: boolean;
}

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: prop => prop !== 'isSelected' && prop !== 'isHovered',
})<CustomPickerDayProps>(({ theme, isSelected, isHovered, day }) => ({
  borderRadius: 0,
  ...(isSelected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.main,
    },
  }),
  ...(isHovered && {
    backgroundColor: theme.palette.primary[theme.palette.mode],
    color: 'inherit',
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary[theme.palette.mode],
      color: '#312E81',
      fontWeight: 800,
    },
  }),
  ...(day.day() === 0 && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
  }),
  ...(day.day() === 6 && {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  }),
})) as React.ComponentType<CustomPickerDayProps>;

const isInSameWeek = (dayA: Dayjs, dayB: Dayjs | null | undefined) => {
  if (dayB == null) {
    return false;
  }

  return dayA.isSame(dayB, 'week');
};

function Day(
  props: PickersDayProps<Dayjs> & {
    selectedDay?: Dayjs | null;
    hoveredDay?: Dayjs | null;
  }
) {
  const { day, selectedDay, hoveredDay, ...other } = props;

  return (
    <CustomPickersDay
      {...other}
      day={day}
      sx={{ px: 2.5 }}
      disableMargin
      selected={false}
      isSelected={isInSameWeek(day, selectedDay)}
      isHovered={isInSameWeek(day, hoveredDay)}
    />
  );
}

export interface SampleWeekPickerProps {
  label: string;
  control: Control<any, any>;
  name: string;
  defaultValue: string | null;
  error: FieldError | undefined;
  helperText: React.ReactNode;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  required?: boolean;
  disabled?: boolean;

  shouldDisableDate?: (day: Dayjs) => boolean;
}

const SampleWeekPicker: React.FC<SampleWeekPickerProps> = ({
  control,
  name,
  defaultValue,
  minDate,
  maxDate,
  disabled = false,
  error,
  helperText,
  required,
  label,

  shouldDisableDate,
}) => {
  const [hoveredDay, setHoveredDay] = React.useState<Dayjs | null>(null);

  return (
    <Grid item>
      <FormControl fullWidth>
        <Controller
          control={control}
          name={name}
          defaultValue={defaultValue ?? ''}
          render={({ field }) => {
            const onChange = (date: any) => {
              field.onChange(dayjs(date).format('YYYY-MM-DD'));
            };

            return (
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="es"
                localeText={
                  esES.components.MuiLocalizationProvider.defaultProps
                    .localeText
                }
              >
                <FormLabel component="legend">{label}</FormLabel>

                <DateCalendar
                  {...field}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={onChange}
                  minDate={minDate}
                  {...(maxDate && { maxDate })}
                  disabled={disabled}
                  showDaysOutsideCurrentMonth={false}
                  displayWeekNumber
                  shouldDisableDate={shouldDisableDate}
                  slots={{ day: Day }}
                  slotProps={{
                    day: ownerState => ({
                      selectedDay: field.value ? dayjs(field.value) : null,
                      hoveredDay,
                      onPointerEnter: () => setHoveredDay(ownerState.day),
                      onPointerLeave: () => setHoveredDay(null),

                      textfield: {
                        // size: 'small',
                        helperText: helperText,
                        error: !!error,
                        required: required,
                      },
                    }),
                  }}
                />
              </LocalizationProvider>
            );
          }}
        />
      </FormControl>
    </Grid>
  );
};

export default SampleWeekPicker;
